import React, { useState, useEffect } from 'react';
import { FiPlus, FiFilter, FiEdit2, FiTrash2, FiGrid, FiShield, FiSearch, FiMapPin, FiLock } from 'react-icons/fi';
import PageComponent from '../../../components/PageComponent';
import Card from '../../../components/Card';
import Table from '../../../components/Table';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import { Skeletons, TableActions } from '../../../components';
import Select from '../../../components/Select';
import Input from '../../../components/Input';
import MultiSelect from '../../../components/MultiSelect';
import RoundModal from './RoundModal';
import QRCodeModal from './QRCodeModal';
import api from '../../../Api';
import { usePermission } from '../../../hooks/usePermission';
import { toast } from 'react-toastify';
import '../../CompanyManager/style.css';
import './style.css';

export default function RoundConfig() {
  const permissions = usePermission('patrol');
  const [rounds, setRounds] = useState([]);
  const [serviceStations, setServiceStations] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);
  const [qrCodeRound, setQrCodeRound] = useState(null);
  const [roundToDelete, setRoundToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Filters
  const [filterStation, setFilterStation] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    async function loadCompanies() {
      if (!permissions.canView) return;
      try {
        const res = await api.get('/companies');
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setCompanies(data);
      } catch (error) {
        console.error(error);
        toast.error('Erro ao carregar empresas.');
      }
    }

    loadCompanies();
  }, []);

  useEffect(() => {
    if (companies.length === 0) return;
    loadData({ showSkeleton: !hasLoadedOnce });
  }, [companies, companyFilter, filterStation, hasLoadedOnce]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setCompanyFilter(idsWithoutAll[0]);
    } else {
      setCompanyFilter('all');
    }
  }

  async function loadData({ showSkeleton = false } = {}) {
    if (companies.length === 0) return;

    try {
      if (showSkeleton) setInitialLoading(true);
      
      const params = {};
      if (filterStation && filterStation !== 'all') {
        params.service_station_id = filterStation;
      }

      let allRounds = [];
      let allStations = [];

      if (companyFilter === 'all') {
        const requests = companies.map(company => 
          Promise.all([
            api.get(`/companies/${company.id}/rounds`, { params }).catch(error => {
              console.error(`Erro ao carregar rondas da empresa ${company.id}:`, error);
              return { data: [] };
            }),
            api.get(`/companies/${company.id}/stations`).catch(error => {
              console.error(`Erro ao carregar postos da empresa ${company.id}:`, error);
              return { data: [] };
            }),
          ]).then(([roundsRes, stationsRes]) => {
            let roundsData = [];
            const responseData = roundsRes.data;

            if (Array.isArray(responseData)) {
              roundsData = responseData;
            } else if (responseData?.data && Array.isArray(responseData.data)) {
              roundsData = responseData.data;
            } else if (responseData?.rounds && Array.isArray(responseData.rounds)) {
              roundsData = responseData.rounds;
            }

            roundsData = roundsData.map(r => ({
              ...r,
              company_id: r.company_id || company.id,
              company,
            }));

            let stationsData = [];
            if (Array.isArray(stationsRes.data)) {
              stationsData = stationsRes.data;
            } else if (stationsRes.data && Array.isArray(stationsRes.data.data)) {
              stationsData = stationsRes.data.data;
            }

            stationsData = stationsData.map(s => ({
              ...s,
              company_id: s.company_id || company.id,
              company,
            }));

            return { rounds: roundsData, stations: stationsData };
          })
        );

        const results = await Promise.all(requests);
        allRounds = results.flatMap(r => r.rounds);
        allStations = results.flatMap(r => r.stations);
      } else {
        const selectedCompany = companies.find(c => String(c.id) === String(companyFilter));
        if (!selectedCompany) {
          setRounds([]);
          setServiceStations([]);
        } else {
          const [roundsRes, stationsRes] = await Promise.all([
            api.get(`/companies/${selectedCompany.id}/rounds`, { params }),
            api.get(`/companies/${selectedCompany.id}/stations`),
          ]);

          let roundsData = [];
          const responseData = roundsRes.data;

          if (Array.isArray(responseData)) {
            roundsData = responseData;
          } else if (responseData?.data && Array.isArray(responseData.data)) {
            roundsData = responseData.data;
          } else if (responseData?.rounds && Array.isArray(responseData.rounds)) {
            roundsData = responseData.rounds;
          } else {
            console.warn('Formato de resposta de rondas desconhecido ou vazio:', responseData);
          }

          roundsData = roundsData.map(r => ({
            ...r,
            company_id: r.company_id || selectedCompany.id,
            company: selectedCompany,
          }));

          let stationsData = [];
          if (Array.isArray(stationsRes.data)) {
            stationsData = stationsRes.data;
          } else if (stationsRes.data && Array.isArray(stationsRes.data.data)) {
            stationsData = stationsRes.data.data;
          }

          stationsData = stationsData.map(s => ({
            ...s,
            company_id: s.company_id || selectedCompany.id,
            company: selectedCompany,
          }));

          allRounds = roundsData;
          allStations = stationsData;
        }
      }

      setRounds(allRounds);
      setServiceStations(allStations);
      setHasLoadedOnce(true);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar dados.');
    } finally {
      if (showSkeleton) setInitialLoading(false);
    }
  }

  function handleCreate() {
    setSelectedRound(null);
    setIsModalOpen(true);
  }

  function handleEdit(round) {
    setSelectedRound(round);
    setIsModalOpen(true);
  }

  function handleRequestDelete(round) {
    setRoundToDelete(round);
    setIsDeleteModalOpen(true);
  }

  async function handleConfirmDelete() {
    if (!roundToDelete || !roundToDelete.company_id) return;
    const companyId = roundToDelete.company_id;
    try {
      setIsDeleting(true);
      await api.delete(`/companies/${companyId}/rounds/${roundToDelete.id}`);
      toast.success('Ronda excluída com sucesso.');
      setIsDeleteModalOpen(false);
      setRoundToDelete(null);
      loadData();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao excluir ronda.');
    } finally {
      setIsDeleting(false);
    }
  }

  function handleCancelDelete() {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setRoundToDelete(null);
  }

  function handleListQRCode(round) {
    if (!round.points || round.points.length === 0) {
      toast.warn('Esta ronda não possui pontos cadastrados.');
      return;
    }
    setQrCodeRound(round);
    setIsQRCodeModalOpen(true);
  }

  const filteredRounds = rounds.filter(round => {
    const term = searchTerm.toLowerCase();
    const name = (round.name || '').toLowerCase();
    const matchesSearch = name.includes(term);

    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);
    const roundCompanyId = round.company_id || round.company?.id;
    const matchesCompany =
      hasAllSelected ||
      specificIds.length === 0 ||
      (roundCompanyId && specificIds.includes(String(roundCompanyId)));

    return matchesSearch && matchesCompany;
  });

  const columns = [
    {
      header: 'Status',
      accessor: 'status',
      render: (round) => (
        <span className={`status-badge ${round.status === 'active' ? 'active' : 'inactive'}`}>
          {round.status === 'active' ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      header: 'Posto de Serviço',
      accessor: 'serviceStation.name',
      render: (round) => round.serviceStation?.name || round.service_station?.name || '-',
    },
    {
      header: 'Nome da Ronda',
      accessor: 'name',
      render: (round) => round.name,
    },
    {
      header: 'Empresa',
      accessor: 'company',
      render: (round) => {
        const company =
          round.company ||
          companies.find(c => String(c.id) === String(round.company_id));
        if (!company) return '-';
        return (
          company.corporate_name ||
          company.trade_name ||
          company.name ||
          `Empresa ${company.id}`
        );
      },
    },
    {
      header: 'Periodicidade',
      accessor: 'periodicity',
      render: (round) => {
        const periodicity = round.periodicity;
        if (!periodicity) return '-';
        const days = Object.entries(periodicity)
          .filter(([_, config]) => config.active)
          .map(([day]) => {
            const map = {
              monday: 'Seg', tuesday: 'Ter', wednesday: 'Qua',
              thursday: 'Qui', friday: 'Sex', saturday: 'Sab', sunday: 'Dom'
            };
            return map[day];
          });
        return days.length > 0 ? days.join(', ') : 'Nenhum dia';
      },
    },
    {
      header: 'Pontos',
      accessor: 'points',
      render: (round) => round.points ? round.points.length : 0,
    },
    {
      header: 'Ações',
      id: 'actions',
      width: '0.6fr',
      render: (round) => (
        <TableActions>
          {permissions.canView && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleListQRCode(round);
              }}
            >
              <FiGrid size={16} />
              QR Codes
            </button>
          )}
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(round);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleRequestDelete(round);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
    },
  ];

  return (
    <PageComponent authenticatedPage={true}>
      {permissions.canView ? (
        <div className="container">
          {initialLoading && <Skeletons.CompanyManagerSkeleton />}

          {!initialLoading && (
            <>
              <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiShield size={22} />
                </div>
                <div>
                  <h1>Configuração de Rondas</h1>
                  <p>Gerencie as rondas, pontos e periodicidade.</p>
                </div>
              </div>
              <div className="manager-header-actions">
                {permissions.canCreate && (
                  <Button onClick={handleCreate} className="manager-header-button">
                    <FiPlus size={18} />
                    Nova Ronda
                  </Button>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {rounds.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredRounds.length} de {rounds.length} rondas
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <MultiSelect
                    id="company-filter"
                    label="Empresa"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({
                        value: String(c.id),
                        label: c.corporate_name || c.trade_name || c.name || `Empresa ${c.id}`,
                      })),
                    ]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>

                <Input
                  id="round-search"
                  label="Buscar"
                  placeholder="Busque por nome"
                  value={searchTerm}
                  onChange={event => setSearchTerm(event.target.value)}
                  icon={FiSearch}
                />

                <Select
                  label="Posto de Serviço"
                  options={[{ value: 'all', label: 'TODOS' }, ...serviceStations.map(s => ({ value: s.id, label: s.name }))]}
                  value={filterStation}
                  onChange={(e) => setFilterStation(e.target.value)}
                  searchable
                  placeholder="SELECIONE..."
                  icon={FiMapPin}
                />
              </div>
            </Card>

            <Table
              columns={columns}
              items={filteredRounds}
              totalItems={filteredRounds.length}
              emptyMessage="Nenhuma ronda cadastrada."
            />

            <RoundModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                initialData={selectedRound}
                serviceStations={serviceStations}
                companies={companies}
                onSuccess={loadData}
                companyId={
                  selectedRound?.company_id ||
                  selectedRound?.company?.id ||
                  (companyFilter !== 'all' ? companyFilter : null)
                }
            />

            <QRCodeModal
                isOpen={isQRCodeModalOpen}
                onClose={() => setIsQRCodeModalOpen(false)}
                round={qrCodeRound}
            />
          </>
        )}
        
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={handleCancelDelete}
          title="Confirmar exclusão"
          closeOnOverlayClick={!isDeleting}
        >
          <div className="delete-modal-content">
            <p>
              Tem certeza que deseja excluir a ronda{' '}
              {roundToDelete && (
                <span className="delete-modal-company-name">
                  {roundToDelete.name}
                </span>
              )}
              ?
            </p>
            <p>
              Essa ação não poderá ser desfeita.
            </p>
          </div>
          <div className="delete-modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        </Modal>
        </div>
      ) : (
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageComponent>
  );
}
